import {
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
} from "@mui/material";
import Check from "@zenith/assets/icons/Check";
import ChevronLeft from "@zenith/assets/icons/ChevronLeft";
import ChevronRight from "@zenith/assets/icons/ChevronRight";
import {
  DoubleArrowSortIcon,
  InflowIcon,
  OutflowIcon,
} from "@zenith/assets/icons/components";
import { NoData3 } from "@zenith/assets/illustrations/NoData3";
import {
  ButtonComponent,
  NestedMenuItemComponent,
  ToastSnackbarComponent,
} from "@zenith/components";
import InfoTooltipComponent from "@zenith/components/ui/InfoTooltipComponent/info-tooltip-component/info-tooltip-component";
import { EmptyState } from "@zenith/components/ui/EmptyStateComponent/empty-state-component/empty-state-component";
import {
  useNotificationContext,
  useTransactionsContext,
} from "@zenith/context";
import {
  NOT_APPLICABLE_TEXT,
  OPERATIONS_EXPORT_FILENAME,
  TRANSACTIONS_DATE_FILTER_FORMAT,
  TRANSACTIONS_DATE_FORMAT,
  TRANSACTIONS_PAGE_SIZE,
  DEFAULT_CURRENCY_SYMBOL,
} from "@zenith/data/constants";
import ServerErrorHandler from "@zenith/features/AppStateHandlers/ServerErrorHandler/server-error-handler";
import {
  AmountTransactionFilter,
  DateTransactionsFilter,
} from "@zenith/features/Transactions/components";
import { OperationsParametersExportRecord } from "@zenith/features/Transactions/interfaces";
import { useOutletView } from "@zenith/features/OutletManagement/hooks/useOutletView";
import { useTablePagination } from "@zenith/hooks";
import {
  TRANSACTIONS_TRANSACTION_DETAILS,
  OPERATION_CONFIRMATION,
  PAYMENT_LINK_CONFIRMATION,
} from "@zenith/routes/paths";
import {
  OperationBaseDTO,
  OperationFullDTO,
  OperationsApiService,
} from "@zenith/services/transactions";
import { usePendingBalance } from "@zenith/features/OutletManagement/hooks/usePendingBalance";
import { usePermissions } from "@zenith/features/AuthZ/usePermissions";
import { formatCurrency, getStatusText } from "@zenith/utils";
import { downloadFileFromBlob } from "@zenith/utils/Helpers/downloadFileUtil";
import dayjs from "dayjs";
import { memo, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { mapPaymentMethodWithType } from "@zenith/utils/ExtensionMethods";

/* eslint-disable-next-line */
export interface OperationsTableComponentProps {
  filters: FiltersData;
  setFilters: React.Dispatch<React.SetStateAction<FiltersData>>;
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  direction: boolean;
  setDirection: React.Dispatch<React.SetStateAction<boolean>>;
  pendingBalance?: number;
  isLoadingOperations?: boolean;
}

type PaymentMethodsType = OperationFullDTO.paymentMethod;
type StatusListType = OperationFullDTO.status;

interface FiltersData {
  maximumAmount: number | undefined;
  minimumAmount: number | undefined;
  paymentMethods: PaymentMethodsType[];
  status: StatusListType[];
  operationDateStart: string | undefined;
  operationDateEnd: string | undefined;
}

interface DatesFilter {
  startDate: unknown;
  endDate: unknown;
}

function getTransactionTypeDisplay(operation: OperationBaseDTO): string {
  return mapPaymentMethodWithType(
    operation.paymentMethod,
    (operation as any).partialTransactionType
  );
}

const OperationsTableComponent = memo(function OperationsTableComponent({
  filters,
  setFilters,
  searchText,
  setSearchText,
  currentPage,
  setCurrentPage,
  direction,
  setDirection,
  pendingBalance = 0,
  isLoadingOperations = false,
}: OperationsTableComponentProps & {
  filters: FiltersData;
  setFilters: React.Dispatch<React.SetStateAction<FiltersData>>;
}) {
  const initialFilters: FiltersData = {
    maximumAmount: undefined,
    minimumAmount: undefined,
    paymentMethods: [],
    status: [],
    operationDateStart: undefined,
    operationDateEnd: undefined,
  };
  const [operations, setOperations] = useState<OperationBaseDTO[]>();

  const {
    transactionsContext,
    setTransactionsContext,
    clearTransactionsContext,
  } = useTransactionsContext();
  const { setOpen } = useNotificationContext();
  const { hasTransactions, isExport, mediaType, totalNoOfTransactions } =
    transactionsContext;
  const { maxPage } = useTablePagination(
    TRANSACTIONS_PAGE_SIZE,
    totalNoOfTransactions as number
  );
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [isNewRequest, setIsNewRequest] = useState<boolean>(true);
  const [dates, setDates] = useState<DatesFilter>({
    startDate: undefined,
    endDate: undefined,
  });
  const [isLoadingOperationsState, setisLoadingOperationsState] =
    useState<boolean>(true);
  const [paymentMethodList, setPaymentMethodList] = useState<
    PaymentMethodsType[]
  >([]);
  const [serverError, setServerError] = useState<boolean>(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const nestedMenuItemClassNames =
    "ml-2.5 py-4 px-2 w-52 hover:rounded-lg hover:bg-25 text-zinc-600";

  const [statusList, setStatusList] = useState<StatusListType[]>([]);

  const { isSingleOutletView, selectedOutlet } = useOutletView();

  // Operations follow the outlet in view. On All Outlets nothing is sent, and
  // the endpoint totals every outlet the caller owns.
  const requestOutletIds =
    isSingleOutletView && selectedOutlet?.id ? [selectedOutlet.id] : undefined;
  const requestOutletIdsKey = requestOutletIds?.join(",") ?? "all";

  /**
   * The pending balance has an endpoint of its own and totals the wallets it is
   * given: the outlet in view's wallet, or every outlet's when the view covers
   * them all.
   */
  /** The prop remains the fallback until the endpoint answers. */
  const displayPendingBalance = usePendingBalance() ?? pendingBalance;
  const { resolved, can } = usePermissions();
  const showsBalance = resolved && can("balance:view");

  // A different outlet is a different result set, so it restarts at page one.
  const previousOutletKeyRef = useRef(requestOutletIdsKey);
  useEffect(() => {
    if (previousOutletKeyRef.current === requestOutletIdsKey) return;
    previousOutletKeyRef.current = requestOutletIdsKey;
    setIsNewRequest(true);
    setCurrentPage(1);
  }, [requestOutletIdsKey, setCurrentPage]);

  const handleTooltipOpen = () => setIsTooltipOpen(true);
  const handleTooltipClose = () => setIsTooltipOpen(false);

  function handleOperationDetails(operation: OperationBaseDTO) {
    const operationId = operation.operationId;
    if (!operationId) return;

    if (operation.status === OperationBaseDTO.status.PENDING) {
      const paymentMethod = operation.paymentMethod;
      const methodStr = String(paymentMethod || "");

      if (
        methodStr === "PAYMENT_LINK" ||
        methodStr === "PAYMENT_LINK_PAY_WITH_TRANSFER" ||
        methodStr === "PAYMENT_LINK_USSD" ||
        methodStr === "PAYMENT_LINK_QR_CODE" ||
        methodStr === "PAYMENT_LINK_CARD"
      ) {
        navigate(PAYMENT_LINK_CONFIRMATION, {
          state: {
            operationId: operationId,
            status: operation.status,
            paymentMethod: operation.paymentMethod,
          },
        });
        return;
      }

      navigate(OPERATION_CONFIRMATION, {
        state: {
          operationId: operationId,
          status: operation.status,
          paymentMethod: operation.paymentMethod,
        },
      });
      return;
    }

    navigate(TRANSACTIONS_TRANSACTION_DETAILS, {
      state: {
        id: operationId,
        isOperation: true,
        filters,
        searchText,
        currentPage,
        direction,
      },
    });
  }

  const getRequestObject = () => {
    const {
      maximumAmount,
      minimumAmount,
      paymentMethods,
      operationDateStart,
      operationDateEnd,
      status,
    } = filters || {};

    const page = currentPage <= 0 ? 0 : currentPage - 1;

    return [
      page.toString(),
      TRANSACTIONS_PAGE_SIZE.toString(),
      direction
        ? OperationsParametersExportRecord.direction.ASC
        : OperationsParametersExportRecord.direction.DESC,
      OperationsParametersExportRecord.orderBy.OPERATION_DATE,
      operationDateStart,
      operationDateEnd,
      paymentMethods?.join(","),
      minimumAmount?.toString(),
      maximumAmount?.toString(),
      status?.join(","),
      requestOutletIds?.join(","),
    ];
  };

  function clearFilters(): void {
    setDates({ startDate: undefined, endDate: undefined });
    setFilters(initialFilters);
    setIsNewRequest(true);
  }

  const handleFiltersButtonClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    if (!open) {
      setAnchorEl(e.currentTarget);
    } else {
      setAnchorEl(null);
    }
  };

  function handlePrevPage(): void {
    if (currentPage > 1) {
      setisLoadingOperationsState(true);
      setIsNewRequest(true);
      setCurrentPage(currentPage - 1);
    }
  }

  function handleNextPage(): void {
    if (currentPage < maxPage) {
      setisLoadingOperationsState(true);
      setIsNewRequest(true);
      setCurrentPage(currentPage + 1);
    }
  }

  function handlePageChange(p: number): void {
    setisLoadingOperationsState(true);
    setIsNewRequest(true);
    setCurrentPage(p);
  }

  function filtersLength(): number {
    let length = 0;

    if (filters.paymentMethods?.length)
      length += filters.paymentMethods?.length;

    if (filters.status?.length) length += filters.status?.length;

    if (filters.maximumAmount || filters.minimumAmount) length++;

    if (filters.operationDateStart || filters.operationDateEnd) length++;

    return length;
  }

  const filterCount = filtersLength();

  function handleClearAllButtonClick(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void {
    clearFilters();
    handleMenuClose();
  }

  function handleMenuClose(): void {
    return setAnchorEl(null);
  }

  function handlePaymentMethodClick(paymentMethod: PaymentMethodsType) {
    const currentMethods = filters.paymentMethods || [];
    if (currentMethods.includes(paymentMethod)) {
      setFilters({
        ...filters,
        paymentMethods: currentMethods.filter((pm) => pm !== paymentMethod),
      });
    } else {
      setFilters({
        ...filters,
        paymentMethods: [...currentMethods, paymentMethod],
      });
    }
  }

  function handleStatusClick(statusSelected: OperationFullDTO.status) {
    setFilters((prevFilters) => {
      const currentStatus = prevFilters.status || [];
      let newStatus;

      if (currentStatus.includes(statusSelected)) {
        newStatus = currentStatus.filter(
          (status: OperationFullDTO.status) => status !== statusSelected
        );
      } else {
        newStatus = [...currentStatus, statusSelected];
      }

      return {
        ...prevFilters,
        status: newStatus,
      };
    });
  }

  function clearDateFilter(): void {
    setDates({ startDate: undefined, endDate: undefined });
    setFilters({
      ...filters,
      operationDateStart: undefined,
      operationDateEnd: undefined,
    });
    setIsNewRequest(true);
  }

  function applyDateFilter(
    operationStartDate: unknown | undefined,
    operationEndDate: unknown | undefined
  ): void {
    setDates({ startDate: operationStartDate, endDate: operationEndDate });
    formatDateToFilters(operationStartDate, operationEndDate);
  }

  function formatDateToFilters(
    operationStartDate: unknown,
    operationEndDate: unknown
  ) {
    let startDate: string | undefined = undefined;
    let endDate: string | undefined = undefined;

    if (
      operationStartDate &&
      typeof operationStartDate === "object" &&
      "$d" in operationStartDate
    ) {
      startDate = dayjs(operationStartDate.$d as string).format(
        TRANSACTIONS_DATE_FILTER_FORMAT
      );
    }

    if (
      operationEndDate &&
      typeof operationEndDate === "object" &&
      "$d" in operationEndDate
    ) {
      endDate = dayjs(operationEndDate.$d as string).format(
        TRANSACTIONS_DATE_FILTER_FORMAT
      );
    }

    setFilters({
      ...filters,
      operationDateStart: startDate,
      operationDateEnd: endDate,
    });
  }

  function clearAmountFilter(): void {
    setFilters({
      ...filters,
      minimumAmount: undefined,
      maximumAmount: undefined,
    });
    setIsNewRequest(true);
  }

  function applyAmountFilter(
    minimumAmount: number | undefined,
    maximumAmount: number | undefined
  ): void {
    setFilters({
      ...filters,
      minimumAmount,
      maximumAmount,
    });
  }

  function handleApplyButtonClick(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void {
    handlePageChange(1);
    setIsNewRequest(true);
    handleMenuClose();
  }

  useEffect(() => {
    OperationsApiService.getListStatus()
      .then((response) => {
        if ("data" in response && response.data) {
          setStatusList(response.data as StatusListType[]);
          setFilters((prevFilters) => ({
            ...prevFilters,
            status: prevFilters.status || [],
          }));
        }
      })
      .catch((error) => {
        throw new Error(error);
      });
  }, []);

  useEffect(() => {
    OperationsApiService.getListPaymentMethod1()
      .then((response) => {
        if ("data" in response && response.data) {
          const filteredPaymentMethods = response.data.filter(
            (pm) => pm !== "IN_BANK_TRANSFER"
          ) as PaymentMethodsType[];
          setPaymentMethodList(filteredPaymentMethods);
        }
      })
      .catch((error) => {
        throw new Error(error);
      });
  }, []);

  useEffect(() => {
    if (isNewRequest) {
      setisLoadingOperationsState(true);
      const operationRequest = getRequestObject();

      OperationsApiService.getOperations(...operationRequest)
        .then((response) => {
          if (response.data) {
            setOperations(response.data.content || []);
            setTransactionsContext({
              ...transactionsContext,
              hasTransactions:
                !!response.data.content?.length &&
                response.data.content?.length > 0,
              totalNoOfTransactions: response.data.totalItems,
              isOperation: true,
            });

            setIsNewRequest(false);
          }
        })
        .catch((error) => {
          if (error.status === 404) {
            clearTransactionsContext();
            setTransactionsContext({
              ...transactionsContext,
              hasTransactions: false,
              totalNoOfTransactions: 0,
              isOperation: true,
            });
            setOperations([]);
            setIsNewRequest(false);
          } else if (typeof error.status === "number" && error.status >= 500) {
            setServerError(true);
          }

          throw new Error(error);
        })
        .finally(() => {
          setisLoadingOperationsState(false);
        });
    }
  }, [direction, currentPage, isNewRequest, requestOutletIdsKey]);

  useEffect(() => {
    if (isExport) {
      const operationRequest = getRequestObject();

      OperationsApiService.exportOperations(mediaType, ...operationRequest)
        .then((response) => {
          downloadFileFromBlob(
            response,
            OPERATIONS_EXPORT_FILENAME + "-" + new Date().getTime().toString(),
            mediaType as OperationsParametersExportRecord.mediaType
          );
          setOpen(true);
        })
        .catch((error) => {
          throw new Error(error);
        })
        .finally(() => {
          setTransactionsContext({ ...transactionsContext, isExport: false });
        });
    }
  }, [isExport]);

  const isSmallScreen = useMediaQuery("(max-width: 640px)");

  const isLoading = isLoadingOperations || isLoadingOperationsState;

  return (
    <>
      <ToastSnackbarComponent
        horizontal="right"
        status={"success"}
        vertical="bottom"
        className="mt-36 ml-40"
        size="sm"
        title={"Export Completed"}
        text={`Your ${transactionsContext.mediaType} was successfully exported.`}
        hasAutoHideDuration
      />
      <div className="mt-8 flex flex-wrap items-center justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <button
            className={`inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 py-2 text-center text-sm font-normal capitalize leading-tight text-zinc-600 ${
              !open
                ? `max-w-103 border border-stone-300 hover:border-neutral-300`
                : `w-108 z-1350 bg-zinc-100 hover:border-neutral-300`
            }`}
            id="filters-button"
            onClick={handleFiltersButtonClick}
          >
            {!open ? "Filters" : "Close Filters"}{" "}
            {!open && filterCount > 0 && (
              <Chip
                className="inline-flex h-6 w-6 flex-col items-center justify-center gap-2.5 rounded-3xl bg-neutral-400 px-1.5 text-center text-xs font-medium text-white"
                label={filterCount}
                size="small"
              />
            )}
          </button>

          {!open && filterCount > 0 && (
            <ButtonComponent
              label="Clear all filters"
              onClick={handleClearAllButtonClick}
              id="clear-all-button"
              size="large"
              type="tertiary"
            />
          )}
        </div>

        {showsBalance && displayPendingBalance > 0 && !isLoading && (
          <div
            className="flex items-center gap-4 rounded-lg border border-neutral-200 px-5 py-2"
            style={{
              minWidth: "300px",
              height: "40px",
              borderRadius: "7px",
              borderWidth: "1px",
              paddingRight: "21px",
              paddingLeft: "21px",
              gap: "15px",
            }}
          >
            <span
              className="whitespace-nowrap text-base font-normal text-[#9B9B9B]"
              style={{ fontFamily: "Outfit", lineHeight: "20px" }}
            >
              Pending balance
            </span>
            <InfoTooltipComponent
              open={isTooltipOpen}
              handleTooltipOpen={handleTooltipOpen}
              handleTooltipClose={handleTooltipClose}
              tooltipId="pending-balance-tooltip"
              content="The pending balance represents the aggregate of payments generated (like payment links/QR codes) but not yet completed by the payer."
              placement="bottom-start"
              size="sm"
              iconColor="#9B9B9B"
            />
            <span
              className="whitespace-nowrap text-base font-normal text-[#1A1C1F]"
              style={{ fontFamily: "Outfit", lineHeight: "20px" }}
            >
              <span className="text-base text-neutral-400">
                {DEFAULT_CURRENCY_SYMBOL}
              </span>{" "}
              {formatCurrency(displayPendingBalance, false)}
            </span>
          </div>
        )}
      </div>

      <Menu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        open={open}
        sx={{
          ".MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded.MuiPopover-paper.MuiMenu-paper.MuiMenu-paper":
            {
              display: "flex",
              width: "229px",
              flexDirection: "column",
              alignItems: "flex-start",
            },
        }}
      >
        <NestedMenuItemComponent
          label="Transaction Type"
          parentMenuOpen={open}
          className={nestedMenuItemClassNames}
          sx={{
            ".MuiTypography-root": {
              fontSize: "14px",
            },
          }}
          filterCount={filters.paymentMethods?.length}
        >
          {paymentMethodList?.map((pm: PaymentMethodsType) => (
            <MenuItem
              className="mx-2.5 h-10 w-64 items-center justify-between gap-3 rounded-lg py-2 pl-3 pr-4 text-sm font-normal leading-none text-zinc-600 hover:bg-stone-50"
              id={`${pm}-filter-button`}
              onClick={() => handlePaymentMethodClick(pm)}
              key={pm}
            >
              {pm.mapPaymentMethod()}
              {filters.paymentMethods.includes(pm) && <Check />}
            </MenuItem>
          ))}
        </NestedMenuItemComponent>

        <NestedMenuItemComponent
          label="Status"
          parentMenuOpen={open}
          className={nestedMenuItemClassNames}
          sx={{
            ".MuiTypography-root": {
              fontSize: "14px",
            },
          }}
          filterCount={(filters.status || []).length}
        >
          {statusList?.map((status: StatusListType) => (
            <MenuItem
              className="mx-2.5 h-10 w-64 items-center justify-between gap-3 rounded-lg py-2 pl-3 pr-4 text-sm font-normal leading-none text-zinc-600 hover:bg-stone-50"
              id={`${status}-filter-button`}
              onClick={() => handleStatusClick(status)}
              key={status}
            >
              {status.mapStatus()}
              {(filters.status || []).includes(status) && <Check />}
            </MenuItem>
          ))}
        </NestedMenuItemComponent>

        <NestedMenuItemComponent
          label="Date"
          parentMenuOpen={open}
          className={nestedMenuItemClassNames}
          sx={{
            ".MuiTypography-root": {
              fontSize: "14px",
            },
          }}
          filterCount={
            filters.operationDateStart || filters.operationDateEnd ? 1 : 0
          }
        >
          <DateTransactionsFilter
            minDate={dates.startDate}
            maxDate={dates.endDate}
            clear={clearDateFilter}
            apply={applyDateFilter}
          />
        </NestedMenuItemComponent>

        <NestedMenuItemComponent
          label="Amount"
          parentMenuOpen={open}
          className={nestedMenuItemClassNames}
          sx={{
            ".MuiTypography-root": {
              fontSize: "14px",
            },
          }}
          filterCount={filters.maximumAmount || filters.minimumAmount ? 1 : 0}
        >
          <AmountTransactionFilter
            maximumValue={filters.maximumAmount}
            minimumValue={filters.minimumAmount}
            clear={clearAmountFilter}
            apply={applyAmountFilter}
          />
        </NestedMenuItemComponent>

        <MenuItem className="mt-3 flex cursor-default flex-wrap justify-between hover:bg-white">
          <button
            className="inline-flex h-9 w-24 items-center justify-center gap-2 rounded-lg px-8 py-2 text-center text-sm capitalize text-zinc-600 hover:text-zinc-900 hover:underline disabled:text-gray-300 disabled:no-underline"
            onClick={handleClearAllButtonClick}
            disabled={!filterCount}
          >
            Clear all
          </button>

          <button
            className="inline-flex h-9 w-24 items-center justify-center gap-2 rounded-lg bg-red-600 px-8 py-2 text-center text-sm text-white disabled:bg-zinc-100 disabled:text-gray-300"
            onClick={handleApplyButtonClick}
            disabled={!filterCount}
          >
            <span>Apply {filterCount > 0 ? `(${filterCount})` : ""}</span>
          </button>
        </MenuItem>
      </Menu>

      <p className="my-4 text-sm text-neutral-600 md:my-8">
        {`Showing ${(!isLoading && operations?.length) || 0} of ${
          (!isLoading && totalNoOfTransactions) || 0
        } Operations`}
      </p>
      <div className="overflow-auto border-t border-neutral-200">
        <Table stickyHeader>
          <TableHead>
            {(totalNoOfTransactions as number) > 0 && (
              <TableRow className="[&>th]:border-b-0 [&>th]:pt-6">
                <TableCell className="w-[5%] min-w-[40px] max-w-[60px] text-sm text-neutral-400">
                  {/* Icon column */}
                </TableCell>
                <TableCell className="w-[15%] min-w-[120px] text-left text-sm text-neutral-400">
                  <div className="flex items-center">
                    <span className="mr-2">Date</span>
                    <button
                      className="cursor-pointer"
                      onClick={() => {
                        setDirection(!direction);
                        setIsNewRequest(true);
                      }}
                      disabled={!hasTransactions}
                    >
                      <DoubleArrowSortIcon />
                    </button>
                  </div>
                </TableCell>
                <TableCell className="w-[30%] min-w-[150px] text-left text-sm text-neutral-400">
                  Transaction type
                </TableCell>
                <TableCell className="w-[25%] min-w-[120px] text-left text-sm text-neutral-400">
                  Status
                </TableCell>
                <TableCell className="w-[25%] min-w-[120px] text-right text-sm text-neutral-400">
                  Amount
                </TableCell>
              </TableRow>
            )}
          </TableHead>
          {hasTransactions && (
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="h-[calc(100vh-600px)] w-full border-b-0"
                  >
                    <div className="flex justify-center text-center">
                      <CircularProgress
                        className={"text-primary-300"}
                        size={"3rem"}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                operations?.map((operation: OperationBaseDTO) => (
                  <TableRow
                    onClick={() =>
                      operation.operationId && handleOperationDetails(operation)
                    }
                    key={`${operation.paymentMethod}-${operation.operationId}`}
                    className="h-16 cursor-pointer hover:bg-stone-50 [&>td]:py-2"
                  >
                    <TableCell className="w-[5%] min-w-[40px] max-w-[60px]">
                      {operation.operationType ===
                      OperationBaseDTO.operationType.OUT ? (
                        <OutflowIcon />
                      ) : (
                        <InflowIcon />
                      )}
                    </TableCell>
                    <TableCell className="w-[15%] min-w-[120px] text-neutral-400">
                      {dayjs(operation.createdAt).format(
                        TRANSACTIONS_DATE_FORMAT
                      )}
                    </TableCell>
                    <TableCell className="w-[30%] min-w-[150px] capitalize text-neutral-600">
                      {getTransactionTypeDisplay(operation)}
                    </TableCell>
                    <TableCell className="w-[25%] min-w-[120px] text-left text-sm text-neutral-600">
                      <div className="flex items-center py-3">
                        {operation.status
                          ? getStatusText(operation.status)
                          : NOT_APPLICABLE_TEXT}
                      </div>
                    </TableCell>
                    {operation.operationType ===
                      OperationBaseDTO.operationType.OUT &&
                      operation.status !== OperationBaseDTO.status.EXPIRED && (
                        <TableCell className="mr-2 w-3/12 min-w-[155px] text-right">
                          <div className="flex flex-col items-end">
                            <p className="text-red-600">
                              - {formatCurrency(operation.totalAmount, true)}
                            </p>
                            {(operation as any).currentAccountBalance !==
                              undefined &&
                              (operation as any).currentAccountBalance !==
                                null &&
                              (operation as any).currentAccountBalance > 0 && (
                                <p className="text-xs text-neutral-400">
                                  {formatCurrency(
                                    (operation as any).currentAccountBalance,
                                    true
                                  )}
                                </p>
                              )}
                          </div>
                        </TableCell>
                      )}
                    {operation.operationType ===
                      OperationBaseDTO.operationType.IN &&
                      operation.status !== OperationBaseDTO.status.EXPIRED && (
                        <TableCell className="mr-2 w-3/12 text-right">
                          <div className="flex flex-col items-end">
                            <p className="text-green-400">
                              + {formatCurrency(operation.totalAmount, true)}
                            </p>
                            {(operation as any).currentAccountBalance !==
                              undefined &&
                              (operation as any).currentAccountBalance !==
                                null &&
                              (operation as any).currentAccountBalance > 0 && (
                                <p className="text-xs text-neutral-400">
                                  {formatCurrency(
                                    (operation as any).currentAccountBalance,
                                    true
                                  )}
                                </p>
                              )}
                          </div>
                        </TableCell>
                      )}
                    {operation.status === OperationBaseDTO.status.EXPIRED && (
                      <TableCell className="mr-2 w-3/12 text-right text-neutral-600 line-through">
                        <div className="flex flex-col items-end">
                          <p className="text-neutral-600">
                            {formatCurrency(operation.totalAmount, true)}
                          </p>
                          {(operation as any).currentAccountBalance !==
                            undefined &&
                            (operation as any).currentAccountBalance !== null &&
                            (operation as any).currentAccountBalance > 0 && (
                              <p className="text-xs text-neutral-400">
                                {formatCurrency(
                                  (operation as any).currentAccountBalance,
                                  true
                                )}
                              </p>
                            )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          )}
        </Table>

        {serverError ? (
          <ServerErrorHandler
            errorTitle="We are unable to fetch transaction history"
            errorMessage="Please try again. Apologies for the inconvenience."
          />
        ) : (
          !isLoading &&
          !hasTransactions && (
            <EmptyState
              title="No Operations Found"
              description={
                filterCount === 0
                  ? "It seems there are no operations to display at the moment"
                  : "No matching items found. Try a different filter."
              }
              illustration={
                <div className="flex w-full justify-center">
                  <NoData3 className="block h-auto w-40 text-primary-300" />
                </div>
              }
            />
          )
        )}
      </div>
      <div className="mt-2 inline-flex h-8 w-full items-center justify-end">
        <div className="flex flex-row">
          <Pagination
            count={maxPage}
            page={currentPage}
            variant="text"
            siblingCount={isSmallScreen ? 0 : 1}
            shape="rounded"
            onChange={(e, p) => handlePageChange(p)}
            hidePrevButton
            hideNextButton
            sx={{
              ".MuiPaginationItem-root.Mui-selected": {
                backgroundColor: "#DA291C",
                color: "white",
              },
              ".MuiPaginationItem-root:hover": {
                backgroundColor: "#DA291C",
                color: "white",
              },
              ".MuiPaginationItem-root.Mui-selected:hover": {
                cursor: "default",
                backgroundColor: "#DA291C",
              },
            }}
          />
          {maxPage > 1 && (
            <>
              <button
                disabled={currentPage === 1}
                className={
                  "flex h-8 w-8 items-center justify-center rounded-md text-center text-sm text-primary-200 hover:bg-primary-200 hover:text-white disabled:text-neutral-400 disabled:hover:bg-transparent"
                }
              >
                <ChevronLeft
                  onClick={handlePrevPage}
                  color={currentPage === 1 ? "text-zinc-900" : "black"}
                />
              </button>
              <button
                disabled={currentPage === maxPage}
                className="flex h-8 w-8 items-center justify-center rounded-md text-center text-sm text-zinc-600 hover:bg-primary-200 hover:text-white disabled:text-neutral-400 disabled:hover:bg-transparent"
              >
                <ChevronRight
                  onClick={handleNextPage}
                  color={currentPage === maxPage ? "text-zinc-900" : "black"}
                />
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
});

export default OperationsTableComponent;
